
import './index.scss';

import React from 'react';
import dayjs from 'dayjs';
import { format as formatDate } from 'date-fns';
import { Avatar, Typography, Card, Table } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { User } from '../../types/user';
import { CheckinData, StylistJob } from '../../types/checkin';
import { BookingData, BookingStatus } from '../../types/booking';
import { getUserFullName } from '../../utils/bookingUtil';

interface Props {
  userProfile: any;
}

class UserProfile extends React.PureComponent<Props> {
  private renderBookingStatus(status: BookingStatus) {
    const statues = ['Pending', 'Booked', 'Canceled', 'Finished', 'Locked', 'Walk In'] as any;
    return statues[status] || '';
  }

  private renderStylistJobs(stylist: StylistJob) {
    return (
      <Card
        size="small"
        title={stylist.stylistName}
        key={`job-${Math.random()}`}
      >
        {(stylist.jobs || []).map(job => job.serviceName).join(', ')}
      </Card>
    );
  }

  private renderUserInfo(clientData: User) {
    return (
      <>
        <div
          className="user--info"
          style={{
            marginTop: 30,
            textAlign: 'center',
          }}
        >
          <div className="user--info--avatar">
            <Avatar
              size={96}
              src={clientData.avatar}
              icon={!clientData.avatar ? <UserOutlined /> : undefined}
            />
          </div>
          <div className="user--info--detail" style={{ marginTop: 20 }}>
            <div className="user--info--name">
              <Typography.Title level={3} style={{ marginBottom: 4 }}>
                {getUserFullName(clientData)}
              </Typography.Title>
            </div>

            <div className="user--info--phone" style={{ fontSize: 16 }}>
              {clientData.phoneNumber}
            </div>
          </div>
        </div>

        <div
          className="user--info--notes"
          style={{
            marginTop: 20,
          }}
        >
          <div
            className="user--info--notes--title"
            style={{
              fontWeight: 600,
              marginBottom: 10,
            }}
          >
            Client notes
          </div>
          <div
            className="user--info--notes--detail"
            style={{ padding: 16, minHeight: 100 }}
          >{clientData.notes || 'No data'}</div>
        </div>
      </>
    );
  }

  private renderUpcomingBooking(upcomingBooking: BookingData) {
    return (
      <div
        className="user--profile-block user--upcoming-booking"
        style={{
          marginTop: 30,
        }}
      >
        <div className="user--profile-block-heading">
          Upcoming booking
        </div>
        <div className="user--profile-block-content">
          <div className="upcoming-booking">
            <div className="upcoming-booking--status">
              {this.renderBookingStatus(upcomingBooking.status)}
            </div>

            <div className="upcoming-booking--date" style={{ fontSize: 16, fontWeight: 600 }}>
              {formatDate(dayjs(upcomingBooking.bookingDate).valueOf(), 'EEEE, MMM dd yyyy')} at {upcomingBooking.bookingTime}
            </div>

            <div className="upcoming-booking--jobs">
              {(upcomingBooking.stylists || []).map(stylist => this.renderStylistJobs(stylist))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  private renderVisitHistory(checkinDatas: CheckinData[]) {
    const visitCount = checkinDatas.length;
    const columns = [
      {
        title: '#',
        dataIndex: 'id',
        key: 'id',
        width: 60,
        render: id => <div style={{ width: 50 }}>{id}</div>,
      },
      {
        title: 'Visit date',
        dataIndex: 'createdDate',
        key: 'createdDate',
        width: 110,
        render: (visitDate, record: CheckinData) => {
          return (
            <div style={{ width: 100 }}>
              {formatDate(dayjs(visitDate).valueOf(), 'MMM dd yyyy')}

              <div>
                {record && record.rewardData.length > 0 && (
                  <Typography.Text type="danger">
                    Visit rewards: {record.rewardData.map(reward => reward.text).join(', ')}
                  </Typography.Text>
                )}
              </div>
            </div>
          );
        },
      },
      {
        title: 'Status',
        dataIndex: 'type',
        key: 'type',
        width: 80,
        render: status => <div style={{ width: 70 }}>{status}</div>,
      },
      {
        title: 'Stylist & services',
        dataIndex: 'stylistJobs',
        key: 'stylistJobs',
        width: 230,
        render: (stylists) => {
          return (
            <div className="user--visit-history--jobs" style={{ width: 220 }}>
              {(stylists || []).map(stylist => this.renderStylistJobs(stylist))}
            </div>
          );
        },
      },
      {
        title: 'Ticket',
        dataIndex: 'invoice',
        key: 'invoice',
        width: 80,
        render: invoice => <div style={{ width: 70 }}>{invoice && invoice.number ? invoice.number : ''}</div>,
      },
      {
        title: 'Forms',
        dataIndex: 'formData',
        key: 'formData',
        width: 150,
        render: formData => {
          return (
            <div style={{ width: 140 }}>
              {(formData || []).map(form => form.name).join(', ')}
            </div>
          );
        },
      },
    ];

    return (
      <div
        className="user--profile-block user--visit-history"
        style={{
          marginBottom: 30,
          marginTop: 30,
        }}
      >
        <div className="user--profile-block-heading">
          Visit history {visitCount > 0 ? `(${visitCount})` : ''}
        </div>
        <div className="user--profile-block-content">
          <Table dataSource={checkinDatas} columns={columns} />
        </div>
      </div>
    );
  }


  render() {
    const { userProfile } = this.props;

    if (userProfile) {
      const { clientData, checkinData, upcomingBooking } = userProfile || {};

      return (
        <div className="user--profile">
          <div className="user--profile--container">
            <div className="user--profile--col user--profile--info">
              {this.renderUserInfo(clientData)}
            </div>

            <div className="user--profile--col user--profile--booking-wrapper">
              {upcomingBooking && (
                <div className="user--profile--upcoming-booking">
                  {this.renderUpcomingBooking(upcomingBooking)}
                </div>
              )}

              <div className="user--profile--visit-history">
                {this.renderVisitHistory(checkinData || [])}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return null;
  }
}

export default UserProfile;
